import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SERVICES } from "../lib/constants";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteDialog({ open, onOpenChange, defaultService }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService || "CCTV Installation",
    address: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      toast.success("Quote request received! We'll call you shortly.");
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
        setForm({
          name: "",
          phone: "",
          email: "",
          service: defaultService || "CCTV Installation",
          address: "",
          message: "",
        });
      }, 1800);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="sm:max-w-lg bg-white"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Request a Free Quote
          </DialogTitle>
          <DialogDescription>
            Share a few details — we'll get back within a few hours.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="quote-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mb-3" />
            <div className="font-display text-xl font-semibold">Request received</div>
            <div className="text-sm text-slate-500 mt-1">
              Our team will reach out to you on the given number shortly.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2" data-testid="quote-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label htmlFor="q-name">Name *</Label>
                <Input
                  id="q-name"
                  data-testid="quote-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your full name"
                />
              </div>
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label htmlFor="q-phone">Phone *</Label>
                <Input
                  id="q-phone"
                  data-testid="quote-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="10-digit mobile"
                  type="tel"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-email">Email (optional)</Label>
              <Input
                id="q-email"
                data-testid="quote-email-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                type="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="quote-service-select">
                  <SelectValue placeholder="Choose service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-address">Address / Area</Label>
              <Input
                id="q-address"
                data-testid="quote-address-input"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                placeholder="Area, locality, Nagpur"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-message">Message</Label>
              <Textarea
                id="q-message"
                data-testid="quote-message-input"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us briefly — number of cameras, site size, preferred visit time..."
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="w-full h-11 rounded-full brand-btn font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Sending..." : "Request Free Quote"}
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              By submitting, you agree to be contacted by Hayat Security for this quote.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
